import { useState, memo, ReactNode } from 'react'
import { FormattedMessage } from 'react-intl'
import { Grid, Theme, Tooltip, styled } from '@mui/material'
import { ICON_SIZE_MEDIUM } from '@carto/meridian-ds/theme'

type IconPalette = 'primary' | 'secondary' | 'default' | 'success'

function getColors(palette: IconPalette, theme: Theme) {
  switch (palette) {
    case 'primary':
      return {
        backgroundColor: theme.palette.primary.relatedLight,

        '& svg path': {
          fill: theme.palette.primary.dark
        }
      }
    case 'secondary':
      return {
        backgroundColor: theme.palette.secondary.relatedLight,

        '& svg path': {
          fill: theme.palette.secondary.dark
        }
      }
    case 'default':
      return {
        backgroundColor: theme.palette.default.main,

        '& svg path': {
          fill: theme.palette.text.primary
        }
      }
    case 'success':
      return {
        backgroundColor: theme.palette.success.relatedLight,

        '& svg path': {
          fill: theme.palette.success.main
        }
      }
  }
}

const GridRoot = styled(Grid, {
  shouldForwardProp: (prop) => prop !== 'palette'
})<{ palette?: IconPalette }>(({ palette, theme }) => ({
  position: 'absolute',
  top: theme.spacingValue * -3.5,
  left: theme.spacingValue * -0.5,
  width: theme.spacing(3),
  height: theme.spacing(3),
  overflow: 'hidden',
  borderRadius: theme.spacing(1.5),
  ...getColors(palette ?? 'default', theme)
}))

const IconWrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'inherit',
  width: theme.spacing(3),
  height: theme.spacing(3),

  '& svg': {
    width: ICON_SIZE_MEDIUM,
    height: 'auto'
  }
}))

export interface CardMiniIconProps {
  icon: ReactNode | undefined
  tooltip?: boolean
  tooltipLabelId?: string
  palette?: IconPalette
  className?: string
}

function CardMiniIcon({ icon, tooltip, tooltipLabelId, palette, className }: CardMiniIconProps) {
  const [showTooltip, setShowTooltip] = useState(false)

  return (
    <GridRoot
      {...(tooltip && {
        onMouseEnter: () => {
          setShowTooltip(true)
        },
        onMouseLeave: () => {
          setShowTooltip(false)
        }
      })}
      container
      alignItems="center"
      palette={palette}
      className={className}
    >
      {showTooltip && tooltipLabelId ? (
        <Tooltip open={showTooltip} title={<FormattedMessage id={tooltipLabelId} />}>
          <IconWrapper>{icon}</IconWrapper>
        </Tooltip>
      ) : (
        <IconWrapper>{icon}</IconWrapper>
      )}
    </GridRoot>
  )
}

export default memo(CardMiniIcon)
